import { canViewIssues } from "@/lib/issues";
import type { Action, MyRole } from "@/types/permissions";

/** A permission check, as `usePermissions` returns it. */
export type Can = (action: Action) => boolean;

/** Whether the user can see a project's photos: either record View verb. */
export function canViewRecords(can: Can): boolean {
  return can("record:View:Self") || can("record:View:Other");
}

/** Whether the user can see a project's reports: either report View verb. */
export function canViewReports(can: Can): boolean {
  return can("report:View:Self") || can("report:View:Other");
}

/**
 * The project's tabs, in the order work moves through them — capture, locate,
 * chase, report — each with what it takes to be shown. Photos and Plans need
 * record viewing (a plan is its pinned photos), Issues an issue View
 * permission, Reports report viewing. See [[bottom-nav.spec]] § Tabs follow
 * permissions.
 */
export const PROJECT_TABS = [
  {
    key: "photos",
    label: "Photos",
    to: "/projects/$projectId/records",
    permitted: canViewRecords,
  },
  {
    key: "plans",
    label: "Plans",
    to: "/projects/$projectId/plans",
    permitted: canViewRecords,
  },
  {
    key: "issues",
    label: "Issues",
    to: "/projects/$projectId/issues",
    permitted: canViewIssues,
  },
  {
    key: "reports",
    label: "Reports",
    to: "/projects/$projectId/reports",
    permitted: canViewReports,
  },
] as const;

export type ProjectTab = (typeof PROJECT_TABS)[number];
export type ProjectTabPath = ProjectTab["to"];

/** The tabs this user may use, in tab order. */
export function permittedTabs(can: Can): ProjectTab[] {
  return PROJECT_TABS.filter((tab) => tab.permitted(can));
}

/**
 * Where a link into a project lands: the first tab the user may use, in tab
 * order — Issues for a contributor. Photos when they may use none, which is
 * where every link went before tabs followed permissions.
 */
export function firstPermittedTab(can: Can): ProjectTabPath {
  return permittedTabs(can)[0]?.to ?? PROJECT_TABS[0].to;
}

/** The tab an app path is in, if any: `/projects/p1/reports/r9` is Reports. */
export function tabForPath(
  pathname: string,
  projectId: string,
): ProjectTab | undefined {
  return PROJECT_TABS.find((tab) => {
    const base = tab.to.replace("$projectId", projectId);
    return pathname === base || pathname.startsWith(`${base}/`);
  });
}

interface ProjectWithRoles {
  owner: string;
  my_roles?: MyRole[];
}

/**
 * `can` for a project payload, outside the project's own pages: the projects
 * list holds a payload per row, not one project.
 *
 * The owner bypasses every check, as in `usePermissions`. While the current
 * user is still unknown, a project with no roles is presumed theirs — owners
 * hold no membership, so no roles — so an owner's tabs, rows and hero don't
 * change under them once their profile arrives. Anyone holding roles is judged
 * by them straight away.
 */
export function projectCan(
  project: ProjectWithRoles,
  currentUserId: string | undefined,
): Can {
  const roles = project.my_roles ?? [];
  const isOwner = currentUserId
    ? currentUserId === project.owner
    : roles.length === 0;
  const actions = new Set(
    roles.flatMap((role) => role.permissions.map((permission) => permission.action)),
  );
  return (action) => isOwner || actions.has(action);
}

/**
 * Whether the user can see a project's photos, from its payload. Projects
 * they can't — a contributor's — are never the projects list's hero, and their
 * rows show only the name and the role chip. See [[project-row.spec]] §
 * Without photo access.
 */
export function canViewProjectPhotos(
  project: ProjectWithRoles,
  currentUserId: string | undefined,
): boolean {
  return canViewRecords(projectCan(project, currentUserId));
}
